import { useEffect, useState } from 'react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import { useSports } from '@/features/reference/useReference';
import type { Theme, ThemePayload } from '@/api/themesService';

interface ThemeModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: ThemePayload) => Promise<unknown>;
  theme: Theme | null;
  isSaving: boolean;
}

const ThemeModal = ({ open, onClose, onSubmit, theme, isSaving }: ThemeModalProps) => {
  const { data: sports = [] } = useSports();
  const [label, setLabel] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [description, setDescription] = useState('');
  const [selectedSports, setSelectedSports] = useState<string[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
      setLabel(theme?.label ?? '');
      setIdentifier(theme?.identifier ?? '');
      setDescription(theme?.description ?? '');
      setSelectedSports(theme?.sports ?? []);
    }
  }, [open, theme]);

  const toggleSport = (identifierValue: string) =>
    setSelectedSports((prev) =>
      prev.includes(identifierValue) ? prev.filter((s) => s !== identifierValue) : [...prev, identifierValue]
    );

  const handleSubmit = async () => {
    setError('');
    if (!label.trim()) return setError('Le libellé est requis.');
    try {
      await onSubmit({
        label: label.trim(),
        identifier: identifier.trim(),
        description: description.trim() || null,
        sports: selectedSports,
      });
      onClose();
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(message ?? 'Enregistrement impossible.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={theme ? 'Modifier la thématique' : 'Ajouter une thématique'}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label="Libellé" placeholder="VO2 max" value={label} onChange={(e) => setLabel(e.target.value)} />
        <Input
          label="Identifiant"
          placeholder="VO2 (auto depuis le libellé si vide)"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
        />
        <Textarea
          label="Description"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sports autorisés</span>
          <div className="flex flex-wrap gap-3">
            {sports.map((s) => (
              <label key={s.identifier} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/40"
                  checked={selectedSports.includes(s.identifier)}
                  onChange={() => toggleSport(s.identifier)}
                />
                {s.label}
              </label>
            ))}
          </div>
        </div>
        {error && <p className="text-sm text-error-500">{error}</p>}
      </div>
    </Modal>
  );
};

export default ThemeModal;
